//src/components/CrushList.tsx

import { useState, useMemo, useRef, forwardRef, useImperativeHandle } from "react";
import toast from 'react-hot-toast';
import { FaPhone, FaEnvelope, FaInstagram, FaFacebook, FaSnapchatGhost, FaLinkedin } from 'react-icons/fa';
import { SiTiktok } from 'react-icons/si';
import { BsThreeDots } from 'react-icons/bs'; // ✅ Add this import
import { ConfirmModal } from './ConfirmModal';
import { CollapsibleSection } from './CollapsibleSection';
import { MemeChooserModal } from './MemeChooserModal';
import { HandleHelpModal } from './HandleHelpModal';
import InfoModal from './InfoModal';
import InfoIcon from './InfoIcon';
import { generateClient } from 'aws-amplify/api';

const client = generateClient();

type Interest = "1" | "2" | "3";

type CrushItem = {
  contact: string;
  interestLevel: Interest;
  contactType?: string;
  status?: "pending" | "confirmed";
  addedAt?: string;
  needsReconfirm?: boolean;  // ✅ ADD THIS
};

//type DmLinks = Partial<Record<string, string>>;

export type CrushListHandle = { focusAddInput: () => void };

interface Props {
  items: CrushItem[];
  onDelete: (contact: string) => void;
  onAdd: (contact: string, interestLevel: Interest, contactType: string) => void;
  onConfirm: (contact: string) => Promise<{ hasMatch?: boolean }>;
  tier?: string;
  userId?: string;
  userZodiacSign?: string | null;
  dmLinks?: Partial<Record<string, string>>;
  reconfirmedCrushes?: Set<string>; // ✅ ADD THIS
  onReconfirm?: (contact: string, interestLevel: Interest, contactType?: string) => Promise<void>; // ✅ ADD THIS
  onNoMatch?: () => void;
}

const MAX_PENDING = 3;

/** Keep only digits (0-9) */
function digitsOnly(s: string) {
  return s.replace(/\D/g, "");
}

/** Format 10 US digits as (XXX) XXX-XXXX while typing */
function formatUSPhone(d: string) {
  const n = digitsOnly(d).slice(0, 10);
  if (n.length < 4) return n;
  if (n.length < 7) return `(${n.slice(0, 3)}) ${n.slice(3, 6)}`;
  return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`;
}
/** Format for display */
function formatPhoneForDisplay(contact: string): string {
  // If it's E.164 format (+1XXXXXXXXXX), format it
  if (contact.startsWith('+1') && contact.length === 12) {
    const digits = contact.slice(2); // Remove +1
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return contact;
}

/** Convert 10 digits to E.164 (+1XXXXXXXXXX) */
function toE164(n10: string) {
  const n = digitsOnly(n10);
  return n.length === 10 ? `+1${n}` : "";
}

const isValidEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

const socialKinds = new Set(["instagram", "tiktok", "snapchat", "facebook", "linkedin"]);

// Normalize social media handle - extract username from URL or handle
function normalizeSocialHandle(_kind: string, input: string): string {
  let cleaned = (input || "").trim();
  
  while (cleaned.startsWith('/')) {
    cleaned = cleaned.slice(1);
  }

  try {
    if (cleaned.includes("://") || cleaned.startsWith("www.")) {
      const url = new URL(cleaned.startsWith("http") ? cleaned : `https://${cleaned}`);
      let pathname = url.pathname.replace(/^\/+/, '');
      
      // ✅ Platform-specific extraction
      const segments = pathname.split('/').filter(Boolean);
      
      // Last segment for all platforms — matches backend normalizeHandle
      pathname = segments[segments.length - 1] || cleaned;
      
      pathname = pathname.split('?')[0].split('#')[0];
      pathname = pathname.startsWith('@') ? pathname.slice(1) : pathname;
      
      return pathname || cleaned;
    }
  } catch {}

  cleaned = cleaned.startsWith("@") ? cleaned.slice(1) : cleaned;
  cleaned = cleaned.split('/')[0].split('?')[0].split('#')[0];
  
  return cleaned;
}

// Generate preview URLs for social platforms
function getSocialPreviewUrl(kind: string, handle: string): string {
  const cleanHandle = handle.startsWith('@') ? handle.slice(1) : handle;
  switch (kind) {
    case "instagram": return `https://instagram.com/${cleanHandle}`;
    case "tiktok": return `https://www.tiktok.com/@${cleanHandle}`;
    case "snapchat": return `https://snapchat.com/add/${cleanHandle}`;
    case "facebook": return `https://facebook.com/${cleanHandle}`;
    case "linkedin": return `https://www.linkedin.com/in/${cleanHandle}`;
    default: return "";
  }
}

const CrushList = forwardRef<CrushListHandle, Props>(function CrushList(
  { items, onDelete, onAdd, onConfirm, tier, userId, userZodiacSign, dmLinks, reconfirmedCrushes, onReconfirm, onNoMatch },
  ref
) {
  const [crushKind, setCrushKind] = useState<string | null>(null);
  const [crushValue, setCrushValue] = useState<string>("");
  const [interestLevel, setInterestLevel] = useState<Interest | null>(null);
  const [viewedCrushes, setViewedCrushes] = useState<Set<string>>(new Set());
  const [showConfirmed, setShowConfirmed] = useState<boolean>(false);
  const pendingSectionRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    focusAddInput: () => inputRef.current?.focus(),
  }));

  const [copied, setCopied] = useState<string | null>(null); // ✅ CHANGED: Track which item was copied
  const [showMemeChooser, setShowMemeChooser] = useState(false);
  const [pendingConfirmCrush, setPendingConfirmCrush] = useState<CrushItem | null>(null);
  const [memes] = useState<any[]>([]);

  // ✅ NEW STATE: Modal management
  const [removingCrush, setRemovingCrush] = useState<string | null>(null);
  const [isRemoveLoading, setIsRemoveLoading] = useState(false);
  const [confirmingKey, setConfirmingKey] = useState<string | null>(null);
  const [helpModalCrush, setHelpModalCrush] = useState<CrushItem | null>(null);

  const [addBtnHovered, setAddBtnHovered] = useState(false);
  const [addBtnPressed, setAddBtnPressed] = useState(false);
  const [btnHov,        setBtnHov]        = useState<string | null>(null);
  const [btnPress,      setBtnPress]      = useState<string | null>(null);

  const pending = useMemo(() => items.filter(i => i.status === "pending"), [items]);
  const confirmed = useMemo(() => items.filter(i => i.status === "confirmed" || !i.status), [items]);

  function isContactValid(): boolean {
    const val = crushValue.trim();
    if (!val || !crushKind) return false;
    if (crushKind === "phone") return digitsOnly(val).length === 10;
    if (crushKind === "email") return isValidEmail(val);
    return true;
  }
  const canAdd = !!crushKind && !!interestLevel && isContactValid();

  const baseKindButton = "relative flex items-center justify-center px-4 py-3 rounded-lg font-medium transition-all";
  const selectedKindButton = "bg-brand text-white shadow-[2px_2px_0px_#D4724A]";
  const unselectedKindButton = "bg-[#E8D4B8] text-[#C96B6B] hover:bg-[#D4B896] focus:outline-none focus:ring-0 active:outline-none outline-none shadow-[2px_2px_0px_#D4724A]";
  const [showPlatformModal, setShowPlatformModal] = useState(false);
  const [vibeInfoOpen, setVibeInfoOpen] = useState(false);

  // ✅ NEW: Platform button configuration
  const allPlatforms = [
    { id: 'snapchat', icon: FaSnapchatGhost, label: 'Snapchat' },
    { id: 'instagram', icon: FaInstagram, label: 'Instagram' },
    { id: 'facebook', icon: FaFacebook, label: 'Facebook' },
    { id: 'phone', icon: FaPhone, label: 'Phone' },
    { id: 'email', icon: FaEnvelope, label: 'Email' },
    { id: 'tiktok', icon: SiTiktok, label: 'TikTok' },
    { id: 'linkedin', icon: FaLinkedin, label: 'LinkedIn' },
  ];

  // ✅ NEW: Platform selection handler
  const handlePlatformSelect = (platformId: string) => {
    setCrushKind(platformId);
    setShowPlatformModal(false);
  };
    
  async function addPendingCrush() {
    let val = crushValue.trim();
    if (!val) return;
    if (!crushKind || !interestLevel) return;

    // Basic format checks
    if (crushKind === "email") {
      if (!isValidEmail(val)) {
        toast.error("Enter a valid email.");
        return;
      }
      // ✅ NORMALIZE: Always lowercase emails  
      val = val.toLowerCase();
    }

    if (crushKind === "phone") {
      const digits = digitsOnly(val);
      if (digits.length !== 10) {
        toast.error("Please enter a valid 10-digit US phone number.");
        return;
      }
      val = toE164(digits);
    }

    // Normalize social media handles
    if (socialKinds.has(crushKind)) {
      try {
        const username = normalizeSocialHandle(crushKind, val);
        val = getSocialPreviewUrl(crushKind, username); // ✅ Store as URL
        if (!val) {
          toast.error("Please enter a valid username or profile URL.");
          return;
        }
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Invalid social media link");
        return;
      }
    }

    // Guard: max pending
    const currPending = items.filter(i => i.status === "pending");
    if (currPending.length >= MAX_PENDING) {
      toast.error("Please confirm or delete a pending crush before adding another.");
      return;
    }

    // Don't duplicate
    if (items.some(i => i.contact === val)) {
      toast.error("This contact is already in your crush list.");
      return;
    }

    // Add to pending
// Auto-detect email vs phone for default contacts
const finalKind = crushKind === "phone" && isValidEmail(val) ? "email" : crushKind;
onAdd(val, interestLevel, finalKind);
    setCrushValue("");
    
    // Scroll to pending section
    setTimeout(() => {
      pendingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }

  // ✅ UPDATED: Copy function tracks which item
  const handleCopy = async (text: string, itemId: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(itemId);
    setTimeout(() => setCopied(null), 2000);
  };

  // ✅ NEW: Handle remove with modal confirmation
  const handleRemoveCrush = async (contact: string) => {
    setIsRemoveLoading(true);
    try {
      await onDelete(contact);
      setRemovingCrush(null);
    } catch (error) {
      void ['Remove crush error:', error];
      toast.error('Failed to remove crush');
    } finally {
      setIsRemoveLoading(false);
    }
  };

async function confirmPending(crush: CrushItem) {
  setPendingConfirmCrush(crush);
  
  // ✅ Confirm and check for match
  const result = await onConfirm(crush.contact);
  
  if (!result?.hasMatch) {
    onNoMatch?.();
  } else {
    // Match detected - skip meme chooser, match popup will show
    void ['✅ Match detected - skipping meme chooser'];
    setPendingConfirmCrush(null);
  }
}

  const handleConfirm = async (x: CrushItem) => {
    const key = x.contact;
    if (confirmingKey === key) return;
    setConfirmingKey(key);
    window.dispatchEvent(new CustomEvent('crushhh:crushConfirmed'));
    try {
      await confirmPending(x);
    } finally {
      setConfirmingKey(null);
    }
  };

  /**
   * ✅ SIMPLIFIED: Just track which memes won (no complex comparison tracking)
   * Updates timesShown for all memes displayed, timesChosen for winner
   */
  async function handleMemeComplete(selectedMeme: any) {
    setShowMemeChooser(false);
    
    if (!pendingConfirmCrush) return;

    // ✅ SIMPLIFIED TRACKING: Just update meme statistics
    if (userId && memes.length >= 4) {
      try {
        // Increment timesShown for all 4 memes that were displayed
        const updatePromises = memes.slice(0, 4).map(async (meme) => {
          const isWinner = meme.memeId === selectedMeme.memeId;
          
          // Fetch current values
          const result = await client.graphql({
            query: /* GraphQL */ `
              query GetMeme($memeId: ID!) {
                getMemeVariant(memeId: $memeId) {
                  memeId
                  timesShown
                  timesChosen
                }
              }
            `,
            variables: { memeId: meme.memeId }
          });

          const current = (result as any).data?.getMemeVariant;
          if (!current) return;

          const newTimesShown = (current.timesShown || 0) + 1;
          const newTimesChosen = isWinner 
            ? (current.timesChosen || 0) + 1 
            : (current.timesChosen || 0);
          const newWinRate = newTimesShown > 0 
            ? newTimesChosen / newTimesShown 
            : 0;

          // Update the meme
          await client.graphql({
            query: /* GraphQL */ `
              mutation UpdateMeme($input: UpdateMemeVariantInput!) {
                updateMemeVariant(input: $input) {
                  memeId
                  timesShown
                  timesChosen
                  winRate
                }
              }
            `,
            variables: {
              input: {
                memeId: meme.memeId,
                timesShown: newTimesShown,
                timesChosen: newTimesChosen,
                winRate: newWinRate,
              }
            }
          });
        });

        await Promise.all(updatePromises);
        
        void ['✅ Meme stats updated:', {
          winner: selectedMeme.memeId,
          totalMemesShown: memes.slice(0, 4).length
        }];
      } catch (error) {
        // Don't block the user flow if tracking fails
        void ['Failed to update meme stats:', error];
      }
    }

    // Now confirm the crush
    setPendingConfirmCrush(null);
  }


  const keyFor = (i: CrushItem) => `${i.contact}-${i.interestLevel}`;

  // ✅ NEW: Check if user is unlimited tier
  const isUnlimited = tier === "unlimited";

return (
  <div className="space-y-6">
    {/* Confirmed Crushes - Only show if there are confirmed crushes */}
    {confirmed.length > 0 && (
      <CollapsibleSection
        title="Your Crushes"
        count={confirmed.length}
        isOpen={showConfirmed}
        onToggle={() => setShowConfirmed(!showConfirmed)}
        headerClassName="w-full flex items-center pr-8 pb-2 pt-3 justify-between text-2xl font-bold font-[Fredoka] text-gray-800 mb-3 hover:text-brand transition-colors focus:outline-none active:outline-none outline-none border-0"
        titleClassName="text-2xl font-bold font-[Fredoka] text-gray-800"
      >
        <ul className="rounded-lg border border-gray-200 bg-transparent">
          {confirmed.map((crush, index) => {
            const interestLabel =
              crush.interestLevel === "3" ? "down bad" :
              crush.interestLevel === "2" ? "like like" : "curious";

            const hearts =
              crush.interestLevel === "3" ? "❤️❤️❤️" :
              crush.interestLevel === "2" ? "❤️❤️" : "❤️";

            return (
              <li key={index} className="px-3 py-2 hover:bg-inherit">
                <div className="text-base font-medium text-gray-700 break-words break-all overflow-wrap">
                  {formatPhoneForDisplay(crush.contact)}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-brand text-white px-2 py-1 text-xs">
                    <span className="w-1.5 h-1.5 rounded-full bg-white" />
                    <span className="capitalize">{crush.contactType ?? "Unknown"}</span>
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-white text-brand px-2 py-1 text-xs">
                    <span aria-hidden="true">{hearts}</span>
                    <span className="sr-only">{interestLevel} interest</span>
                    <span>{interestLabel}</span>
                  </span>
                  <span className="flex-1" />
                  
                  {/* ✅ UPDATED: Only show Re-confirm if crush needs it */}
                  {tier === 'paid' && crush.needsReconfirm && onReconfirm && !reconfirmedCrushes?.has(crush.contact) && (
                    <button
                      onClick={() => onReconfirm(crush.contact, crush.interestLevel, crush.contactType)}
                      className="shrink-0 rounded border border-orange-300 bg-orange-500 text-white font-medium hover:bg-orange-600 px-3 py-2 text-sm"
                    >
                      Confirm
                    </button>
                  )}
                  
                  <button
                    onClick={() => setRemovingCrush(crush.contact)}
                    onMouseEnter={() => setBtnHov(`remove-${crush.contact}`)}
                    onMouseLeave={() => { setBtnHov(null); setBtnPress(null); }}
                    onMouseDown={() => setBtnPress(`remove-${crush.contact}`)}
                    onMouseUp={() => setBtnPress(null)}
                    style={{
                      fontFamily: "'Fredoka', sans-serif",
                      fontWeight: 600,
                      fontSize: 14,
                      background: btnPress === `remove-${crush.contact}` ? "rgba(0,0,0,0.12)" : btnHov === `remove-${crush.contact}` ? "rgba(0,0,0,0.06)" : "#FFFFFF",
                      color: "#1A1A1A",
                      border: "3px solid #1A1A1A",
                      borderRadius: 100,
                      boxShadow: "4px 4px 0px #1A1A1A",
                      padding: "4px 14px",
                      cursor: "pointer",
                      transform: btnPress === `remove-${crush.contact}` ? "scale(0.97)" : "scale(1)",
                      transition: "all 0.1s ease",
                    }}
                  >
                    Remove
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </CollapsibleSection>
    )}

    {/* Pending Crushes - Only show if there are pending crushes */}
    {pending.length > 0 && (
      <div ref={pendingSectionRef}>
        <h3
          className="text-lg text-gray-800 mb-3"
          style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 700 }}
        >
          Pending Verification
        </h3>
        <ul className="space-y-3">
          {pending.map((x, idx) => {
            const k = keyFor(x);
            const isSocial = socialKinds.has(x.contactType || "");
            const previewUrl = isSocial 
              ? (x.contact.startsWith('http') ? x.contact : getSocialPreviewUrl(x.contactType || "", x.contact))
              : "";
            const interestLabel =
              x.interestLevel === "3" ? "down bad" :
              x.interestLevel === "2" ? "like like" : "curious";

            const hearts =
              x.interestLevel === "3" ? "❤️❤️❤️" :
              x.interestLevel === "2" ? "❤️❤️" : "❤️";

return (
              <li key={idx} className="rounded-lg border-2 border-brand hover:bg-transparent bg-none p-4 mt-1 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 break-all">{formatPhoneForDisplay(x.contact)}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1 rounded-full text-gray-700 px-2 py-0.5 text-xs">
                        <span className="bg-brand" />
                        <span className="capitalize">{x.contactType ?? "Unknown"}</span>
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full text-brand px-2 py-0.5 text-xs">
                        <span aria-hidden="true">{hearts}</span>
                        <span>{interestLabel}</span>
                      </span>
                    </div>
                  </div>

                  {/* Copy button */}
                  <div className="relative group flex-shrink-0">
                    <button
                      onClick={() => handleCopy(previewUrl, x.contact)}
                      className="text-xs p-2 bg-neutral-50 rounded hover:bg-gray-300 transition-colors"
                    >
                      {copied === x.contact ? (
                        <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                      )}
                    </button>
                    <span className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-2 py-1 text-xs text-white bg-gray-900 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                      {copied === x.contact ? "Copied!" : "Copy"}
                    </span>
                  </div>

                  {/* Remove button */}
                  <div className="relative group flex-shrink-0">
                    <button
                      onClick={() => setRemovingCrush(x.contact)}
                      onMouseEnter={() => setBtnHov(`removeX-${x.contact}`)}
                      onMouseLeave={() => { setBtnHov(null); setBtnPress(null); }}
                      onMouseDown={() => setBtnPress(`removeX-${x.contact}`)}
                      onMouseUp={() => setBtnPress(null)}
                      style={{
                        fontFamily: "'Fredoka', sans-serif",
                        fontWeight: 600,
                        fontSize: 14,
                        background: btnPress === `removeX-${x.contact}` ? "rgba(0,0,0,0.12)" : btnHov === `removeX-${x.contact}` ? "rgba(0,0,0,0.06)" : "#FFFFFF",
                        color: "#1A1A1A",
                        border: "3px solid #1A1A1A",
                        borderRadius: 100,
                        boxShadow: "4px 4px 0px #1A1A1A",
                        padding: "4px 14px",
                        cursor: "pointer",
                        transform: btnPress === `removeX-${x.contact}` ? "scale(0.97)" : "scale(1)",
                        transition: "all 0.1s ease",
                      }}
                    >
                      ✕
                    </button>
                    <span className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-2 py-1 text-xs text-white bg-gray-900 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                      Remove
                    </span>
                  </div>

                </div>

                {isSocial && previewUrl && (
                  <div className="space-y-2 mt-5">                        
                    {!viewedCrushes.has(k) ? (
                      <>
                      <button
                        onClick={() => {
                          window.open(previewUrl, "_blank");
                          setViewedCrushes(prev => new Set(prev).add(k));
                        }}
                        onMouseEnter={() => setBtnHov(`view-${x.contact}`)}
                        onMouseLeave={() => { setBtnHov(null); setBtnPress(null); }}
                        onMouseDown={() => setBtnPress(`view-${x.contact}`)}
                        onMouseUp={() => setBtnPress(null)}
                        style={{
                          width: "100%",
                          padding: "12px 24px",
                          background: btnPress === `view-${x.contact}` ? "#B34428" : btnHov === `view-${x.contact}` ? "#C94E30" : "#E05A3A",
                          color: "#FFFFFF",
                          fontFamily: "'Fredoka', sans-serif",
                          fontWeight: 700,
                          fontSize: 18,
                          border: "3px solid #1A1A1A",
                          borderRadius: 100,
                          boxShadow: "4px 4px 0px #1A1A1A",
                          cursor: "pointer",
                          transform: btnPress === `view-${x.contact}` ? "scale(0.97)" : "scale(1)",
                          transition: "all 0.1s ease",
                        }}
                      >
                        Click to View & Confirm Profile
                      </button>

                      <button
                        onClick={() => setHelpModalCrush(x)}
                        style={{
                          display: "block",
                          width: "100%",
                          marginTop: 8,
                          padding: "6px 0",
                          background: "none",
                          border: "none",
                          fontFamily: "'Nunito', sans-serif",
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#888",
                          cursor: "pointer",
                          textDecoration: "underline",
                          textUnderlineOffset: 3,
                        }}
                      >
                        Having trouble viewing their profile?
                      </button>
                      </>
                    ) : (
                      <>
                      <button
                        onClick={() => handleConfirm(x)}
                        disabled={confirmingKey === x.contact}
                        onMouseEnter={() => confirmingKey !== x.contact && setBtnHov(`confirm-${x.contact}`)}
                        onMouseLeave={() => { setBtnHov(null); setBtnPress(null); }}
                        onMouseDown={() => confirmingKey !== x.contact && setBtnPress(`confirm-${x.contact}`)}
                        onMouseUp={() => setBtnPress(null)}
                        style={{
                          width: "100%",
                          padding: "12px 24px",
                          background: (btnPress === `confirm-${x.contact}` && confirmingKey !== x.contact) ? "#B34428" : (btnHov === `confirm-${x.contact}` && confirmingKey !== x.contact) ? "#C94E30" : "#E05A3A",
                          color: "#FFFFFF",
                          fontFamily: "'Fredoka', sans-serif",
                          fontWeight: 700,
                          fontSize: 18,
                          border: "3px solid #1A1A1A",
                          borderRadius: 100,
                          boxShadow: "4px 4px 0px #1A1A1A",
                          cursor: confirmingKey === x.contact ? "default" : "pointer",
                          opacity: confirmingKey === x.contact ? 0.65 : 1,
                          transform: (btnPress === `confirm-${x.contact}` && confirmingKey !== x.contact) ? "scale(0.97)" : "scale(1)",
                          transition: "all 0.1s ease",
                        }}
                      >
                        {confirmingKey === x.contact ? "Confirming..." : "✓ Confirm Crushhh"}
                      </button>

                      <button
                        onClick={() => setHelpModalCrush(x)}
                        style={{
                          display: "block",
                          width: "100%",
                          marginTop: 8,
                          padding: "6px 0",
                          background: "none",
                          border: "none",
                          fontFamily: "'Nunito', sans-serif",
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#888",
                          cursor: "pointer",
                          textDecoration: "underline",
                          textUnderlineOffset: 3,
                        }}
                      >
                        Having trouble viewing their profile?
                      </button>
                      </>
                    )}
                  </div>
                )}

                {!isSocial && (
                  <button
                    onClick={() => handleConfirm(x)}
                    disabled={confirmingKey === x.contact}
                    onMouseEnter={() => confirmingKey !== x.contact && setBtnHov(`confirm-${x.contact}`)}
                    onMouseLeave={() => { setBtnHov(null); setBtnPress(null); }}
                    onMouseDown={() => confirmingKey !== x.contact && setBtnPress(`confirm-${x.contact}`)}
                    onMouseUp={() => setBtnPress(null)}
                    style={{
                      width: "100%",
                      padding: "12px 24px",
                      background: (btnPress === `confirm-${x.contact}` && confirmingKey !== x.contact) ? "#B34428" : (btnHov === `confirm-${x.contact}` && confirmingKey !== x.contact) ? "#C94E30" : "#E05A3A",
                      color: "#FFFFFF",
                      fontFamily: "'Fredoka', sans-serif",
                      fontWeight: 700,
                      fontSize: 18,
                      border: "3px solid #1A1A1A",
                      borderRadius: 100,
                      boxShadow: "4px 4px 0px #1A1A1A",
                      cursor: confirmingKey === x.contact ? "default" : "pointer",
                      opacity: confirmingKey === x.contact ? 0.65 : 1,
                      transform: (btnPress === `confirm-${x.contact}` && confirmingKey !== x.contact) ? "scale(0.97)" : "scale(1)",
                      transition: "all 0.1s ease",
                    }}
                  >
                    {confirmingKey === x.contact ? "Confirming..." : "✓ Confirm Crushhh"}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    )}

      {/* Add new crush */}
      <div className="space-y-3 pt-4 border-t-2 border-gray-300">
        <label
          className="block text-gray-800 text-lg"
          style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 600 }}
        >
          Add a Crush
        </label>
        
        {/* ✅ DESKTOP: Show all 7 buttons (hidden on mobile) */}
        <div className="hidden sm:grid grid-cols-4 gap-2">
          {allPlatforms.map(platform => {
            const Icon = platform.icon;
            const isLinkedIn = platform.id === 'linkedin';
            return (
              <button
                key={platform.id}
                onClick={() => setCrushKind(platform.id)}
                title={platform.label}
                className={`${baseKindButton} ${
                  crushKind === platform.id ? selectedKindButton : unselectedKindButton
                }`}
                style={isLinkedIn ? { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'none', flexDirection: 'column', gap: 2 } : {}}
              >
                <Icon size={24} />
                {isLinkedIn && (
                  <span style={{ fontFamily: "'Nunito', sans-serif", fontSize: 10, color: "#1A1A1A" }}>
                    Coming soon
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* ✅ MOBILE: Show 5 buttons + More button */}
        <div className="grid grid-cols-3 gap-2 sm:hidden">
          {/* First 5 platforms */}
          {allPlatforms.slice(0, 5).map(platform => {
            const Icon = platform.icon;
            return (
              <button
                key={platform.id}
                onClick={() => setCrushKind(platform.id)}
                title={platform.label}
                className={`${baseKindButton} ${
                  crushKind === platform.id ? selectedKindButton : unselectedKindButton
                }`}
              >
                <Icon size={24} />
              </button>
            );
          })}

          {/* More... button */}
          <button
            onClick={() => setShowPlatformModal(true)}
            title="More platforms"
            className={`${baseKindButton} ${unselectedKindButton}`}
          >
            <BsThreeDots size={24} />
          </button>
        </div>

        {/* Interest level selector - stays the same */}
        <div className="space-y-2">
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <label
              className="text-lg text-gray-700"
              style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 600 }}
            >
              Choose Your Vibe
            </label>
            <button
              type="button"
              onClick={() => setVibeInfoOpen(true)}
              aria-label="How vibe matching works"
              style={{ background: "none", border: "none", cursor: "pointer", padding: "0 0 0 2px", verticalAlign: "middle", lineHeight: 1 }}
            >
              <InfoIcon size={15} />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => setInterestLevel("1")}
              className="font-medium transition-all focus:outline-none"
              style={{
                fontFamily: "'Fredoka', sans-serif",
                fontWeight: 600,
                fontSize: 13,
                border: "2px solid #1A1A1A",
                borderRadius: 100,
                padding: "8px 4px",
                textAlign: "center",
                background: interestLevel === "1" ? "#FFFFFF" : "#F0DFC8",
                color: "#1A1A1A",
                boxShadow: interestLevel === "1" ? "1px 1px 0px #1A1A1A" : "3px 3px 0px #1A1A1A",
                lineHeight: 1.3,
                cursor: "pointer",
                transition: "all 0.1s ease",
                boxSizing: "border-box",
              }}
            >
              <span style={{ display: "block" }}>❤️</span>
              <span style={{ display: "block" }}>curious</span>
            </button>
            <button
              onClick={() => setInterestLevel("2")}
              className="font-medium transition-all focus:outline-none"
              style={{
                fontFamily: "'Fredoka', sans-serif",
                fontWeight: 600,
                fontSize: 13,
                border: "2px solid #1A1A1A",
                borderRadius: 100,
                padding: "8px 4px",
                textAlign: "center",
                background: interestLevel === "2" ? "#FFFFFF" : "#F0DFC8",
                color: "#1A1A1A",
                boxShadow: interestLevel === "2" ? "1px 1px 0px #1A1A1A" : "3px 3px 0px #1A1A1A",
                lineHeight: 1.3,
                cursor: "pointer",
                transition: "all 0.1s ease",
                boxSizing: "border-box",
              }}
            >
              <span style={{ display: "block" }}>❤️❤️</span>
              <span style={{ display: "block" }}>like like</span>
            </button>

            <button
              onClick={() => setInterestLevel("3")}
              className="font-medium transition-all focus:outline-none"
              style={{
                fontFamily: "'Fredoka', sans-serif",
                fontWeight: 600,
                fontSize: 13,
                border: "2px solid #1A1A1A",
                borderRadius: 100,
                padding: "8px 4px",
                textAlign: "center",
                background: interestLevel === "3" ? "#FFFFFF" : "#F0DFC8",
                color: "#1A1A1A",
                boxShadow: interestLevel === "3" ? "1px 1px 0px #1A1A1A" : "3px 3px 0px #1A1A1A",
                lineHeight: 1.3,
                cursor: "pointer",
                transition: "all 0.1s ease",
                boxSizing: "border-box",
              }}
            >
              <span style={{ display: "block" }}>❤️❤️❤️</span>
              <span style={{ display: "block" }}>Down Bad</span>
            </button>
          </div>
          <p style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: 15,
            fontWeight: 600,
            color: "rgba(26,26,26,0.5)",
            margin: "16px 0 4px 0",
            textAlign: "center",
          }}>
            Your vibe never overshoots theirs.
          </p>
          {vibeInfoOpen && (
            <InfoModal onClose={() => setVibeInfoOpen(false)}>
              <p style={{ margin: "0 0 10px" }}>
                <strong>The reveal only shows what you both share.</strong>
              </p>
              <p style={{ margin: 0 }}>
                Say you&apos;re down bad and they&apos;re just curious, they&apos;ll only ever see curious.{" "}
                <strong>You&apos;ll never look more into it than they do — and they&apos;ll never look more into it than you do.</strong>{" "}
                That&apos;s the whole point.
              </p>
            </InfoModal>
          )}
        </div>

        {/* Input + Add button — always visible */}
        <div className="space-y-2 p-4 bg-none rounded-lg">
          <input
            ref={inputRef}
            className="w-full border bg-white rounded-lg p-3"
            type={crushKind === "phone" ? "tel" : crushKind === "email" ? "email" : "text"}
            inputMode={crushKind === "phone" ? "numeric" : undefined}
            autoComplete={crushKind === "phone" ? "tel" : crushKind === "email" ? "email" : "off"}
            placeholder={
              !crushKind                ? "Add Your Crush" :
              crushKind === "phone"     ? "(555) 123-4567" :
              crushKind === "email"     ? "their.email@example.com" :
                                          `username or ${crushKind} handle`
            }
            value={crushKind === "phone" ? formatUSPhone(crushValue) : crushValue}
            onChange={e => {
              if (crushKind === "phone") {
                const digits = digitsOnly(e.target.value).slice(0, 10);
                setCrushValue(digits);
              } else if (crushKind !== null && socialKinds.has(crushKind)) {
                setCrushValue(e.target.value.toLowerCase());
              } else {
                setCrushValue(e.target.value);
              }
            }}
            style={{ fontFamily: "'Nunito', sans-serif" }}
          />
          <button
            onClick={addPendingCrush}
            disabled={!canAdd}
            onMouseEnter={() => canAdd && setAddBtnHovered(true)}
            onMouseLeave={() => { setAddBtnHovered(false); setAddBtnPressed(false); }}
            onMouseDown={() => canAdd && setAddBtnPressed(true)}
            onMouseUp={() => setAddBtnPressed(false)}
            style={{
              width: "100%",
              padding: "12px 24px",
              background: addBtnPressed ? "#B34428" : addBtnHovered ? "#C94E30" : "#E05A3A",
              color: "#FFFFFF",
              fontFamily: "'Fredoka', sans-serif",
              fontWeight: 700,
              fontSize: 18,
              border: "3px solid #1A1A1A",
              borderRadius: 100,
              boxShadow: "4px 4px 0px #1A1A1A",
              transform: addBtnPressed ? "scale(0.97)" : "scale(1)",
              cursor: canAdd ? "pointer" : "not-allowed",
              opacity: canAdd ? 1 : 0.45,
              transition: "all 0.1s ease",
            }}
          >
            Add Crush
          </button>
        </div>
      </div>

      {/* ✅ NEW: Platform Selection Modal (Mobile) */}
      {showPlatformModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl border-4 border-gray-950 p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3
                className="text-xl text-gray-600"
                style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 700 }}
              >
                Choose Platform
              </h3>
              <button 
                onClick={() => setShowPlatformModal(false)} 
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {allPlatforms.map(platform => {
                const Icon = platform.icon;
                const isLinkedIn = platform.id === 'linkedin';
                return (
                  <button
                    key={platform.id}
                    onClick={() => handlePlatformSelect(platform.id)}
                    className={`${baseKindButton} ${crushKind === platform.id ? selectedKindButton : unselectedKindButton} flex-col gap-1`}
                    style={isLinkedIn ? { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'none' } : {}}
                  >
                    <Icon size={24} />
                    <span style={{ fontSize: 11, fontFamily: "'Fredoka', sans-serif" }}>{platform.label}</span>
                    {isLinkedIn && (
                      <span style={{ fontFamily: "'Nunito', sans-serif", fontSize: 9, color: "#1A1A1A" }}>
                        Coming soon
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ✅ NEW: Confirmation Modal */}
<ConfirmModal
  isOpen={removingCrush !== null}
  onClose={() => setRemovingCrush(null)}
  onConfirm={() => {
    if (removingCrush) {
      handleRemoveCrush(removingCrush);
    }
  }}
  title="Remove Crush?"
  message={
    pending
      ? "Remove this pending crush?" // ✅ Simple for pending
      : isUnlimited
        ? "Are you sure you want to remove this crush?"
        : "This will free up a slot but you cannot undo this action."
  }
  confirmText="Remove"
  confirmButtonClass="bg-red-600 hover:bg-red-700"
  isLoading={isRemoveLoading}
/>

    <MemeChooserModal
      isOpen={showMemeChooser}
      onClose={() => {
        setShowMemeChooser(false);
        setPendingConfirmCrush(null);
      }}
      onComplete={handleMemeComplete}
      memes={memes}
      dmLinks={dmLinks}
      userZodiacSign={userZodiacSign}
    />

    {helpModalCrush && (
      <HandleHelpModal
        platform={helpModalCrush.contactType || null}
        handle={helpModalCrush.contact}
        onConfirmed={() => {
          const crush = helpModalCrush;
          setHelpModalCrush(null);
          handleConfirm(crush);
        }}
        onReEnter={() => setHelpModalCrush(null)}
        onDismiss={() => setHelpModalCrush(null)}
      />
    )}

    </div>
  );
});

export default CrushList;